import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

interface BreadcrumbSegment {
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}

interface BreadcrumbContextValue {
  breadcrumbs: BreadcrumbSegment[];
  setBreadcrumbs: (segments: BreadcrumbSegment[]) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  breadcrumbs: [],
  setBreadcrumbs: () => {},
});

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbSegment[]>([]);

  const setBreadcrumbs = useCallback((segments: BreadcrumbSegment[]) => {
    setBreadcrumbsState(segments);
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbs(segments?: BreadcrumbSegment[]) {
  const { breadcrumbs, setBreadcrumbs } = useContext(BreadcrumbContext);
  const serialized = segments
    ? JSON.stringify(segments.map(s => ({ label: s.label, href: s.href })))
    : null;
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  useEffect(() => {
    if (segmentsRef.current) {
      setBreadcrumbs(segmentsRef.current);
    }
    return () => {
      setBreadcrumbs([]);
    };
  }, [serialized, setBreadcrumbs]);

  return { breadcrumbs, setBreadcrumbs };
}

export type { BreadcrumbSegment };
